import {ButtonHTMLAttributes, MouseEvent, ReactNode, useEffect, useRef, useState} from 'react';
import Spinner from './feedback/Spinner';

// The one button. Same look as the .btn classes it wraps, plus the thing every tap on a
// server needs: while the work runs it shows a ring, swaps its words for `busyLabel` when one
// is given, and ignores further taps. Hand it `busy` from a mutation (`create.isPending`), or
// return a promise from `onClick` and it tracks the work itself.
//
//   <Button onClick={() => save()} busy={save.isPending} busyLabel="Saving…">Save</Button>
//   <Button variant="secondary" danger onClick={async () => { await remove(); }}>Delete</Button>

export type ButtonVariant = 'primary' | 'secondary' | 'ghost';

type Props = Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'onClick'> & {
    variant?: ButtonVariant;
    size?: 'md' | 'sm';
    /** Rust text, for the destructive option (pairs with variant="secondary"). */
    danger?: boolean;
    /** Controlled busy state; a promise-returning onClick sets it on its own. */
    busy?: boolean;
    /** Replaces the children while busy ("Saving…"); the ring shows either way. */
    busyLabel?: ReactNode;
    block?: boolean;
    onClick?: (event: MouseEvent<HTMLButtonElement>) => unknown | Promise<unknown>;
    children: ReactNode;
};

const VARIANT: Record<ButtonVariant, string> = {
    primary: 'btn btn-primary',
    secondary: 'btn btn-secondary',
    ghost: 'btn'
};

export default function Button({variant = 'primary', size = 'md', danger = false, busy, busyLabel, block = false, onClick, children, className = '', disabled, type = 'button', ...rest}: Props) {
    const [pending, setPending] = useState(false);
    const mounted = useRef(true);
    useEffect(() => {
        mounted.current = true;
        return () => {
            mounted.current = false;
        };
    }, []);

    const isBusy = busy ?? pending;

    const handle = (event: MouseEvent<HTMLButtonElement>) => {
        if (isBusy || !onClick) return;
        const result = onClick(event);
        if (result instanceof Promise) {
            setPending(true);
            result.finally(() => {
                if (mounted.current) setPending(false);
            });
        }
    };

    return (
        <button
            type={type}
            className={`${VARIANT[variant]} ${size === 'sm' ? 'btn-sm' : ''} ${danger ? 'btn-danger' : ''} ${block ? 'w-full' : ''} ${className}`}
            disabled={disabled || isBusy}
            aria-busy={isBusy || undefined}
            onClick={handle}
            {...rest}
        >
            {isBusy && <Spinner size="sm" label="Working" />}
            {isBusy && busyLabel !== undefined ? busyLabel : children}
        </button>
    );
}
